import { z } from "zod";

const personSchema = z.object({
  name: z.string(),
  age: z.number().nullish(),
  occupation: z.string().nullish(),
  gender: z.enum(['male', 'female', 'other']).nullish(),
  education: z.string().nullish(),
  hometown: z.string().nullish(),
  languages: z.array(z.string()).nullish(),
  smoker: z.boolean().nullish(),
  pets: z.array(z.string()).nullish()
});

const communicationPreferencesSchema = z.object({
  preferredCommunicationChannel: z.enum(['WhatsApp', 'Telegram', 'Email', 'Phone']).nullish(),
  preferredCommunicationDesc: z.string().nullish(),
  contactNumber: z.string().nullish(),
  email: z.string().email().nullish(),
  social_media_profiles: z.object({
    instagram: z.string().nullish(),
    facebook: z.string().nullish(),
    linkedin: z.string().nullish()
  }).nullish()
});

export const userProfileSchema = z.object({
  id: z.string().uuid(),
  created_at: z.string(),
  updated_at: z.string(),
  persons: z.array(personSchema).nullish(),
  reason_for_moving: z.string().nullish(),
  current_situation: z.string().nullish(),
  size_preferences: z.object({
    minSize: z.number().nullish(),
    maxSize: z.number().nullish(),
    unit: z.string().nullish()
  }).nullish(),
  room_preferences: z.object({
    minRooms: z.number().nullish(),
    maxRooms: z.number().nullish()
  }).nullish(),
  location_preferences: z.array(z.string()).nullish(),
  location_preference_areas: z.array(z.string()).nullish(),
  budget_preferences: z.object({
    min: z.number().nullish(),
    max: z.number().nullish(),
    currency: z.string().nullish()
  }).nullish(),
  preferred_communication_channel: z.enum(['WhatsApp', 'Telegram']).nullish(),
  contact_number: z.string().nullish(),
  soft_factors: z.string().nullish(),
  hobbies: z.string().nullish(),
  interests: z.string().nullish(),
  preferred_flatmate_gender: z.enum(['male', 'female', 'mixed', 'any']).nullish(),
  preferred_flatmate_age_range: z.object({
    min: z.number().nullish(),
    max: z.number().nullish()
  }).nullish(),
  cleanliness_level: z.enum(['very clean', 'clean', 'average', 'relaxed']).nullish(),
  social_level: z.enum(['very social', 'social', 'balanced', 'private']).nullish(),
  move_in_date: z.string().nullish(),
  length_of_stay: z.string().nullish(),
  looking_for_shared_flat: z.boolean().nullish(),
  previous_shared_flat_experience: z.boolean().nullish(),
  communication_preferences: communicationPreferencesSchema.nullish(),
  is_onboarding_complete: z.boolean().catch(false),
  language: z.string().catch('en'),
  email_consent: z.boolean().catch(false),
  documents_available: z.array(z.string()),
  profile_picture_url: z.string().nullish(),
});

type UserProfile = z.infer<typeof userProfileSchema>;

export default UserProfile;

export const AVAILABLE_DOCUMENTS = {
  SCHUFA: 'schufa',
  BEWERBERMAPPE: 'bewerbermappe',
  EMPLOYMENT_CONTRACT: 'employment_contract',
  INCOME_STATEMENT: 'income_statement',
} as const;

export function generateDefaultUserProfile(id: string): Partial<UserProfile> {
  return {
    id,
    persons: [{ name: '', age: 0, occupation: '' }], // Minimum of one person required
    reason_for_moving: '',
    current_situation: '',
    size_preferences: {
      minSize: 0,
      maxSize: 0,
      unit: 'sqm'
    },
    room_preferences: {
      minRooms: 1,
      maxRooms: 1
    },
    location_preferences: [],
    budget_preferences: {
      min: 0,
      max: 0,
      currency: '€'
    },
    soft_factors: '',
    preferred_communication_channel: 'Telegram', // Changed to Telegram as default
    contact_number: '',
  };
}




